import {
  FluidAnalyticsInterface,
  FluidButtonConfig,
  FluidButtonType,
  FluidColorScheme,
  FluidDataLayerComponentConfiguration,
  FluidHorizontalPosition,
  FluidIconConfig,
  FluidKeyedObject,
  FluidPopoverConfig,
  FluidPopoverTranslationOptions,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidButtonTranslations } from '@lmig/fluid-core/lib/i18n/types/temp-element-translations';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { GLOBAL_HTML_ATTRS } from '../../constants/constants';
import {
  hasTranslationAtKey,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { initializeAttrTunnel } from '../../utils/elements/html.utils';
import {
  propSync,
  validatePopoverProp,
} from '../../utils/logging/deprecation.utils';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import {
  ButtonIcon,
  ButtonLabel,
  ButtonPopover,
} from './components/fluid-button.components';

/**
 * @displayName Button
 *
 * @contextData FLUID Buttons represent actions that users can initiate within an experience.
 *
 * @slot icon-before - Named slot for icon BEFORE the Button label. Only available if "iconBefore" Prop is NOT specified.
 * @slot icon-after - Named slot for icon AFTER the Button label. Only available if "iconAfter" Prop is NOT specified.
 * @slot icon-self - Named slot for an icon-only Button.
 *
 * @builder true
 *
 * @analyticsAware ['blur', 'click', 'hover']
 */
@Component({
  tag: 'fluid-button',
  styleUrl: 'fluid-button.less',
  shadow: true,
})
export class FluidButton
  implements ComponentInterface, FluidThemeInterface, FluidAnalyticsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  private _popoverConfig: FluidPopoverConfig & {
    fluidTranslate?: FluidPopoverConfig;
  };

  /**
   * Array of supported native HTML attributes that can be tunneled from
   * the host element to the root <button> tag.
   */
  private _supportedHtmlAttrs: string[] = [
    ...GLOBAL_HTML_ATTRS,
    'type',
    'value',
  ];

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to host element - we need listen to hover events, even
   * on disabled buttons.
   */
  @Element() host: HTMLFluidButtonElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidButtonTranslations & {
    popoverConfig?: FluidPopoverTranslationOptions;
  };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;

  // ================================================================ //
  // -- States

  /**
   * Object containing key/value pairs for each native HTML attribute
   * that has been "tunneled" from "host" element to inner <button> tag.
   * Used/updated by "initializeAttrTunnel()" method.
   */
  @State() _attrMap: FluidKeyedObject<string> = {};

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * The label text displayed on the Button OR the "aria-label" applied
   * to Icon-Only Buttons. NOTE: when using the "icon" Prop, you should
   * still provide a descriptive label to this Prop for accessibility.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the color & style of the Button. NOTE: "primary" Buttons should
   * be used very sparingly, & should only be used for the most important
   * actions in the user's workflow (i.e. you should NOT have 2+ "primary"
   * Buttons side-by-side).
   *
   * @variant
   */
  @Prop({ mutable: true }) type: FluidButtonType = FluidButtonType.PRIMARY;

  @Watch('type')
  onTypeChange() {
    propSync(this, 'type', 'color', true);
  }

  /**
   * NOTE: This Prop applies to Icon-Only Buttons (i.e. "icon" Prop) &
   * "type: ghost" Buttons ONLY (it will be ignored for all other Button
   * types). Sets color of Button's label text and/or embedded Icon to
   * "light" or "dark" for increased contrast when displayed on a color
   * background. For example, if your Button is displayed on a DARK BLUE
   * background, set this Prop's value to "light" so that the label color
   * will be white (instead of the default blue). If your Button is
   * displayed on a YELLOW background, set this Prop's value to "dark" so
   * that the label will be dark gray (instead of the default blue). Do
   * NOT change this Prop's value if your Button is displayed on a WHITE
   * background. See "Examples" tab for more info.
   */
  @Prop({ mutable: true }) colorScheme: FluidColorScheme =
    FluidColorScheme.DEFAULT;

  /**
   * If true, Button will be disabled (i.e. adds 'disabled' attribute).
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * Removes ALL default margins (i.e. "margin-right" & "margin-bottom").
   */
  @Prop({ mutable: true }) removeMargin = false;

  /**
   * Removes default right margin.
   */
  @Prop({ mutable: true }) removeHorizontalMargin = false;

  /**
   * Removes default bottom margin.
   */
  @Prop({ mutable: true }) removeVerticalMargin = false;

  /**
   * When provided, adds a Popover to Button that is displayed on :hover
   * (e.g. display a message on disabled Button). Accepts either a
   * string or FluidPopoverConfig value - string value generates a simple
   * Popover, while a FluidPopoverConfig value allows all Popover Props
   * to be customized.
   */
  @Prop({ mutable: true }) popoverConfig: string | FluidPopoverConfig;

  @Watch('popoverConfig')
  onPopoverConfigChange() {
    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      undefined,
      this.fluidTranslate?.popoverConfig
    );
  }

  /**
   * When true, Button will be smaller than the default size.
   */
  @Prop({ mutable: true }) small = false;

  /**
   * When true, Button will be larger than the default size.
   */
  @Prop({ mutable: true }) large = false;

  /**
   * When provided, Button will display an embedded Icon ONLY instead of
   * a label (i.e. creates an Icon-Only Button). NOTE: even though a
   * label isn't displayed when this Prop is used, you should still
   * provide a descriptive label to the "label" Prop for accessibility
   * (i.e. the "label" Prop value is applied as an "aria-label" to the
   * <button> element).
   */
  @Prop({ mutable: true }) icon: FluidIconConfig;

  /**
   * If the button is part of a control bar, what position is it
   * in? 'start' | 'middle' | 'end'
   */
  @Prop({ mutable: true }) controlBarPosition: FluidHorizontalPosition;

  /**
   * An additional icon to be placed before the button label
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * An additional icon to be placed after the button label
   */
  @Prop({ mutable: true }) iconAfter: FluidIconConfig;

  /**
   * Allows all Button Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidButtonConfig;

  @Prop({ mutable: true }) identifier: string;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: selectiveMapConfigToLocal - set local @Props to "config" values
   * @action: propSync - keeps deprecated "color" Prop in-sync with "type" Prop
   * @action: validatePopoverProp - map deprecated "popover" Prop to "popoverConfig"
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    propSync(this, 'type', 'color', isNullOrUndefined(this.color));
    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      this.popover,
      this.fluidTranslate?.popoverConfig
    );
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: validateButtonContent - ensure that label or icon is provided
   * @action: initializeAttrTunnel - tunnels native HTML attrs from "host" element to <button> tag
   * @action: validatePopoverProp
   */
  componentWillLoad(): void {
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
    this.validateButtonContent();
    initializeAttrTunnel(this, this._supportedHtmlAttrs);
    if (
      this.popoverConfig ||
      this.popover ||
      this.fluidTranslate?.popoverConfig
    ) {
      this.popoverConfig = validatePopoverProp(
        this.host,
        this.popoverConfig,
        this.popover,
        this.fluidTranslate?.popoverConfig
      );
    }
  }

  /**
   * Called once just after the component fully loaded and the first `render()` occurs.
   */
  componentDidLoad(): void {
    this.setupAnalytics();
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listen for click event on the host element, this to ensure
   * the event doesn't bubble if the button is disabled.
   * @param event - the click event
   */
  @Listen('click', { capture: true })
  handleClick(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    } else {
      sendEvent(this.host, this.parentLayerId, 'click');
    }
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Ensures that a label and/or icon (via @Prop or slot) is provided
   * to display within the Button. Outputs dev warning if neither
   * are specified.
   */
  validateButtonContent(): void {
    const hasSlottedIcon = this.host.querySelector('[slot^="icon-"]');
    if (
      !this.icon &&
      !this.label &&
      !this.iconBefore &&
      !this.iconAfter &&
      !hasSlottedIcon &&
      !hasTranslationAtKey<FluidButtonTranslations>(
        this.fluidTranslate,
        this._i18nStore,
        'label'
      )
    ) {
      Utils.consoleWarn(this.host, 'Label and/or icon must be provided');
    }
  }

  /**
   * Returns true if Button has a Popover AND Popover has "message".
   */
  hasPopover(): boolean {
    return (
      !!this._popoverConfig?.message ||
      !!this._popoverConfig?.fluidTranslate?.message
    );
  }

  /**
   * Returns true if Button configuration supports setting the color scheme
   * (i.e. Ghost or Icon-Only Buttons ONLY).
   */
  hasColorScheme(): boolean {
    return this.type === FluidButtonType.GHOST || !!this.icon;
  }

  /**
   * Maps deprecated Popover Props and/or properties from
   * "popoverConfig" Prop to a common FluidPopoverConfig object used
   * to render Button's embedded Popover.
   */
  getPopoverConfig(): FluidPopoverConfig {
    if (!this.hasPopover()) return;
    return {
      header: this.popoverTitle,
      width: this.popoverWidth,
      fluidTranslate: (this.popoverConfig as FluidPopoverConfig)
        ?.fluidTranslate,
      ...this._popoverConfig, // "_popoverConfig" overrides individual Popover Props
    };
  }

  /**
   * Opens Popover & sends event to FLUID Data Layer when Button
   * receives :focus.
   */
  handleFocus() {
    sendEvent(this.host, this.parentLayerId, 'focus');
  }

  /**
   * Closes Popover & sends event to FLUID Data Layer when Button
   * loses :focus.
   */
  handleBlur() {
    sendEvent(this.host, this.parentLayerId, 'blur');
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const iconButton =
      !!this.icon || !!this.host.querySelector('[slot="icon-self"]');
    const buttonClass = {
      [`fluid-btn ${this.theme}`]: true,
      [this.type]: !iconButton,
      [`color-scheme-${this.colorScheme}`]: this.hasColorScheme(),
      'no-margin': this.removeMargin || iconButton,
      'no-vertical-margin': this.removeVerticalMargin,
      'no-horizontal-margin': this.removeHorizontalMargin,
      [`control-bar-${this.controlBarPosition}`]: !!this.controlBarPosition,
      'btn-small': this.small,
      'btn-large': this.large,
      'form-element-height': this.alignedWithFormElement,
      'btn-disabled': this.disabled,
      'icon-only':
        (!this.label &&
          !hasTranslationAtKey<FluidButtonTranslations>(
            this.fluidTranslate,
            this._i18nStore,
            'label'
          )) ||
        iconButton,
      'icon-button': iconButton, // ONLY applied when "icon" Prop OR slot="icon-self" used
      'inherit-icon-color': this.inheritIconColor,
    };

    const buttonProps = {
      disabled: this.disabled,
      id: this.config?.identifier || this.identifier || this.host.id,
      slot: this.hasPopover() ? 'trigger' : null,
      ...(!!this.host.id && { id: this.host.id }),
      onFocus: () => this.handleFocus(),
      onBlur: () => this.handleBlur(),
      onMouseEnter: () => this.handleFocus(),
      onMouseLeave: () => this.handleBlur(),
    };

    return (
      <ButtonPopover host={this} identifier={this.identifier}>
        <button
          {...buttonProps}
          {...this._attrMap}
          class={buttonClass}
          aria-label={iconButton ? this.label : null}
          part={!!this.inheritIconColor && !this.disabled ? 'button' : null}
          data-test-id={`${this.identifier}-button-wrapper`}>
          <ButtonIcon
            config={this.icon}
            host={this.host}
            position='self'
            identifier={this.identifier}
          />
          <ButtonIcon
            config={this.iconBefore}
            host={this.host}
            position='before'
            identifier={this.identifier}
          />
          <ButtonLabel
            label={
              this._i18nStore?.translate(this.fluidTranslate?.label) ||
              this.label
            }
            iconButton={iconButton}
            identifier={this.identifier}
          />
          <ButtonIcon
            config={this.iconAfter}
            host={this.host}
            position='after'
            identifier={this.identifier}
          />
        </button>
      </ButtonPopover>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * When true, indicates that Button is adjacent to a form element &
   * should have the same height as the form field.
   * @internal
   */
  @Prop({ mutable: true }) alignedWithFormElement: boolean;

  /**
   * When true, indicates that Icon-Only Button should inherit its Icon
   * color from the parent component / element.
   * @internal
   */
  @Prop({ mutable: true }) inheritIconColor: boolean;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(this.host)?.id;
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "color" is now deprecated. Instead, use the "type" Prop
   * (same functionality, just a different naming convention) -- Sets the
   * color & style of the Button.
   * @deprecated
   */
  @Prop({ mutable: true }) color: FluidButtonType;

  @Watch('color')
  onColorChange() {
    propSync(this, 'type', 'color');
  }

  /**
   * DEPRECATED: "popover" is now deprecated (https://chromestatus.com/feature/5463833265045504).
   * Instead, use the "message" property of the "popoverConfig" Prop. --
   * If provided, adds simple "tooltip" Popover to Button that is
   * displayed on hover. Typically used to display a message when Button
   * is disabled. NOTE: if you need to customize Popover's Props, use the
   * "popoverConfig" Prop instead to access ALL of Popover's Props.
   * @deprecated
   */
  @Prop({ mutable: true }) popover: string;

  @Watch('popover')
  onPopoverChange() {
    if (this.popover === null) return;
    this._popoverConfig = validatePopoverProp(
      this.host,
      undefined,
      this.popover,
      this.fluidTranslate?.popoverConfig
    );
  }

  /**
   * DEPRECATED: "popoverTitle" is now deprecated. Instead, use the
   * "header" property of the "popoverConfig" Prop. -- Adds a title to the
   * Popover. If undefined, Popover will show without a header section.
   * @deprecated
   */
  @Prop({ mutable: true }) popoverTitle: string;

  /**
   * DEPRECATED: "popoverWidth" is now deprecated. Instead, use the
   * "width" property of the "PopoverConfig" Prop. -- Specifies the maximum
   * width of the Popover (in px).
   * @deprecated
   */
  @Prop({ mutable: true }) popoverWidth: number;
}
